// src/components/PdfRenderer.js
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
  PDFDownloadLink,
  Font,
} from '@react-pdf/renderer';
import { driverPhotoURL } from '../utils/data';
import { useState } from 'react';
import { useEffect } from 'react';
import { countryOptions, mxStates } from '../utils/constants/countryOptions';

// Wrap long words so big names don't overflow lines
Font.registerHyphenationCallback(word => [word]);

const S = StyleSheet.create({
  page: {
    paddingTop: 64,
    paddingBottom: 84,
    paddingHorizontal: 12,
    fontSize: 9,
    fontFamily: 'Helvetica',
  },
  header: {
    marginBottom: 10,
    borderBottom: '1pt solid #000',
    paddingBottom: 6,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
  },
  title: { fontSize: 14, fontWeight: 700 },
  meta: { fontSize: 9, marginTop: 2 },

  // NOTE: header is fixed (overlay), so we pad the body from top & bottom
  body: { marginTop: 10, paddingBottom: 70 },

  sectionTitle: { fontSize: 11, fontWeight: 700, marginTop: 10, marginBottom: 4 },

  // Grid (single values row under a gray header)
  table: { borderLeft: '1pt solid #000', borderTop: '1pt solid #000', marginBottom: 8 },
  tr: { flexDirection: 'row' },
  th: {
    flex: 1,
    borderRight: '1pt solid #000',
    borderBottom: '1pt solid #000',
    padding: 4,
    fontWeight: 700,
    backgroundColor: '#e6e6e6',
  },
  td: {
    flex: 1,
    borderRight: '1pt solid #000',
    borderBottom: '1pt solid #000',
    padding: 4,
    flexShrink: 1,
    // overflow: 'hidden',
  },

  // Multi-row list table
  thSmall: {
    borderRight: '1pt solid #000',
    borderBottom: '1pt solid #000',
    padding: 4,
    fontWeight: 700,
    backgroundColor: '#e6e6e6',
    fontSize: 9,
    flexGrow: 1,
  },
  tdSmall: {
    borderRight: '1pt solid #000',
    borderBottom: '1pt solid #000',
    padding: 4,
    fontSize: 9,
    flexGrow: 1,
    // overflow: 'hidden',
  },
  rowWrap: { flexDirection: 'row', flexWrap: 'nowrap' },

  footer: { position: 'absolute', bottom: 24, left: 42, right: 42, fontSize: 8, marginTop: 30, paddingTop: 20 },
  disclaimer: { marginTop: 6, fontStyle: 'italic', color: 'red' },
  pageNum: { position: 'absolute', bottom: 24, right: 42, fontSize: 8 },
});

// adds invisible breakpoints for long unbroken strings (no '-' will appear)
const softWrap = (input, chunkSize) => {
  const s = val(input);

  // If it already has spaces, let normal wrapping do its job.
  if (!s || s.length <= chunkSize || /\s/.test(s)) return s;

  // Insert newline breaks for long tokens.
  const chunks = [];
  for (let i = 0; i < s.length; i += chunkSize) {
    chunks.push(s.slice(i, i + chunkSize));
  }
  return chunks.join('\n');
};
// ---------- Base layout (header, optional photo, footer w/ disclaimer) ----------
function Base({ title, transactionId, queryLine, messages, driverPhotoBase64, children }) {
  const [photoSrc, setPhotoSrc] = useState(null);

  useEffect(() => {
    async function loadPhoto() {
      if (driverPhotoBase64) {
        const dataUrl = await driverPhotoURL(driverPhotoBase64);
        setPhotoSrc(dataUrl);
      }
    }
    loadPhoto();
  }, [driverPhotoBase64]);
  const dateTime = new Date().toLocaleString('en-US', {
    year: 'numeric', 
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
  const messagesText =
    Array.isArray(messages) && messages.length > 0
      ? messages.join('; ')
      : null;
  return (
    <Document>
      <Page size="A4" style={S.page}>
        {/* Header on every page */}
        <View style={S.header} fixed>
          <View>
            <Text style={S.title}>{title}</Text>
            {queryLine ? <Text style={S.meta}>{queryLine}</Text> : null}
            <Text style={S.meta}>Transaction ID: {transactionId || 'N/A'}</Text>
          </View>
          {driverPhotoBase64 ? (
            <Image
              src={photoSrc}
              style={{ width: 80, height: 80, marginLeft: 8 }}
            />
          ) : null}
        </View>

        <View style={S.body}>
            {messagesText ? (
              <Text
                style={[S.disclaimer, { marginBottom: 8 }]}
                color="red"
                render={({ pageNumber }) =>
                  pageNumber === 1 ? messagesText : ''
                }
              />
            ) : null}

            {children}
          </View>

        {/* Footer on every page */}
        <Text
          style={S.pageNum}
          render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`}
          fixed
        />
        <View style={S.footer} fixed>
          <Text>CDLIS.DOT.GOV</Text>
          <Text>{dateTime}</Text>
          <Text style={S.disclaimer}>
            Disclaimer: This document contains sensitive information and is for official use only.
            Handle and secure this information according to FMCSA privacy and security guidelines.
            Properly dispose of this document when no longer needed.
          </Text>
        </View>
      </Page>
    </Document>
  );
}

// ---------- Shared helpers ----------
const val = v => (v === undefined || v === null ? '' : String(v));
const splitOrSelf = (raw) => {
  if (!raw) return '';
  const parts = String(raw).split(' - ');
  return parts[1] || raw;
};
const eyeFmt = raw => {
  if (!raw) return '';
  const parts = String(raw).split('-');
  return parts[1] ? `${parts[1]} - ${raw}` : raw;
};
const hasItems = x => Array.isArray(x) ? x.length > 0 : !!x;

// Single-row grid table (labels row + values row)
function GridTable({type = "", title, columns, values }) {
  if (!columns || columns.length === 0) return null;
  const wrapWidth = type === "alias" || type === "DGI" ? 10 : 16;
  return (
    <View wrap={false} minPresenceAhead={36}>
      {title ? <Text style={S.sectionTitle}>{title}</Text> : null}
      <View style={S.table}>
        {/* Header */}
        <View style={S.tr}>
          {columns.map((h, i) => (
            <Text key={`h-${i}`} style={S.th}>{h}</Text>
          ))}
        </View>
        {/* Values */}
        <View style={S.tr}>
          {values.map((v, i) => (
            <Text key={`v-${i}`} style={S.td} wrap>{softWrap(v, wrapWidth)}</Text>
          ))}
        </View>
      </View>
    </View>
  );
}

// Row-based table (label + value per row)
function RowTable({ title, rows }) {
  if (!rows || rows.length === 0) return null;
  return (
    <View wrap={false} minPresenceAhead={36}>
      {title ? <Text style={S.sectionTitle}>{title}</Text> : null}
      <View style={S.table}>
        {rows.map((r, i) => (
          <View key={i} style={S.tr}>
            <Text style={S.th}>{r.label}</Text>
            <Text style={S.td} wrap>{softWrap(r.value, 25)}</Text>
          </View>
        ))}
      </View>
    </View>
  );
}

// Multi-row list table (for Convictions / Accidents / Withdrawals / etc.)
function ListTable({ title, columns, rows }) {
  if (!rows || rows.length === 0) return null;
  return (
    <View>
      {title ? <Text style={S.sectionTitle}>{title}</Text> : null}
      <View style={S.table}>
        <View style={S.rowWrap}>
          {columns.map((c, i) => (
            <Text key={`th-${i}`} style={[S.thSmall, { flexBasis: c.width || 'auto', flexGrow: c.grow ?? 1 }]}>
              {c.header}
            </Text>
          ))}
        </View>
        {rows.map((r, idx) => (
          <View key={`r-${idx}`} style={S.rowWrap}>
            {columns.map((c, i) => (
              <Text
                key={`td-${idx}-${i}`}
                style={[S.tdSmall, { flexBasis: c.width || 'auto', flexGrow: c.grow ?? 1 }]}
                
              >
                {c.render ? c.render(r) : softWrap(r[c.key], 14)}
              </Text>
            ))}
          </View>
        ))}
      </View>
    </View>
  );
}

// ---------- Section renderers for PDF ----------
function SectionMedicalCert({ med, ex, exName, medRest }) {
  if (!med) return null;
  const medColumns = [
    ...(med.MedicalNumber ? ['Medical Number'] : []),
    'Issue Date',
    'Expiration Date',
    'SPE Issue',
    'SPE Expiration'
  ];
  const medValues = [
    ...(med.MedicalNumber ? [med.MedicalNumber] : []),
    med.IssueDate,
    med.ExpirationDate,
    med.SPE_EffectiveDate,
    med.SPE_ExpDate,
  ];
  
  const columns = [{ header: 'Value', key: 'RestrictionText', render: r => r.RestrictionText || r.Text || r.Value || r.Code || '' }];
  return (
    <>
      <GridTable
        title="Medical Certification"
        columns={medColumns}
        values={medValues}
      />
      <GridTable
        title=""
        columns={['Med WE Issue','Med WE Expire Date','Status Code','Self Certification']}
        values={[
          med.Waiver_Or_Exempt_EffectiveDate,
          med.Waiver_Or_Exempt_ExpDate,
          splitOrSelf(med.StatusCode || med.Status),
          splitOrSelf(med.SelfCertificationCode || med.SelfCertification),
        ]}
      />
      {hasItems(medRest) && 
      <>
      {/* <Text style={S.sectionTitle}>Medical Certificate Restrictions</Text> */}
      <ListTable title="Medical Certificate Restrictions" columns={columns} rows={medRest} />
      </>
      }
      {hasItems(ex) ? (
        <>
          <Text style={S.sectionTitle}>Medical Examiner Detail</Text>
          <GridTable
            title=""
            columns={['First Name','Middle Name','Last Name','Suffix','Registry Number']}
            values={[exName?.FirstName, exName?.MiddleName, exName?.LastName, exName?.Suffix, ex.RegistryNumber]}
          />
          <GridTable
            title=""
            columns={['Medical #','Specialty','State','Phone']}
            values={[ex.MedicalNumber || ex.LicenseNumber, splitOrSelf(ex.SpecialityCode || ex.Specialty), ex.JurisCode || ex.State, ex.TelephoneNumber || ex.Phone]}
          />
        </>
      ) : null}
    </>
  );
}

function SectionEndorsements({ items }) {
  if (!items || items.length === 0) return null;
  const columns = [{ header: 'Value', key: 'EndorsementText' }];
  const rows = items.map(it => (typeof it === 'string' ? { EndorsementText: it } : it));
  return <ListTable title="Endorsements" columns={columns} rows={rows} />;
}

function SectionRestrictions({ items }) {
  if (!items || items.length === 0) return null;
  const columns = [{ header: 'Value', key: 'RestrictionText', render: r => r.RestrictionText || r.Text || r.Value || r.Code || '' }];
  return <ListTable title="License Restrictions" columns={columns} rows={items} />;
}

function SectionPermits({ items }) {
  if (!items || items.length === 0) return null;
  return (
    <View>
      {items.map((permit, i) => (
        <View key={`pr-${i}`} wrap={false} minPresenceAhead={120}>
          <GridTable
            title={`Permit Class ${permit.CommercialClassCode || ''}`}
            columns={['Issue Date','Expiration Date','Status']}
            values={[permit.IssueDate, permit.ExpirationDate, splitOrSelf(permit.Status)]}
          />
          {permit.Restrictions && permit.Restrictions.length > 0 ? (
            <ListTable
              title={`Permit Class ${permit.CommercialClassCode || ''} Restrictions`}
              columns={[{ header: 'Value', key: 'RestrictionText', render: r => r.RestrictionText || r.Text || r.Value || r.Code || '' }]}
              rows={permit.Restrictions}
            />
          ) : null}
        </View>
      ))}
    </View>
  );
}

function SectionHazmat({ haz }) {
  if (!haz || !haz.TsaHazMatThreatDetermination) return null;
  return (
    <GridTable
      title="Hazmat Determination"
      columns={['TSA Hazmat Determination','Hazmat Expiration Date']}
      values={[haz.TsaHazMatThreatDetermination, haz.ExpirationDate]}
    />
  );
}

function SectionConvictions({ list }) {
  const rows = Array.isArray(list) ? list : [];
  if (rows.length === 0) return null;

  return (
    <View>
      {rows.map((r, i) => (
        <View key={`conv-${i}`} >
          <Text style={S.sectionTitle}>Conviction {i + 1}</Text>

          {/* Row 1 */}
          <GridTable
            title=""
            columns={[
              'Citation Date',
              'Conviction Date',
              'Conviction ID',
              'State of Conviction',
              'Court Type',
              'ACD Code',
            ]}
            values={[
              r.CitationDate,
              r.ConvictionDate,
              r.ConvictionIdCode,
              r.JurisdictionCodeConvicting,
              splitOrSelf(r.ConvictionCourtType),
              r.ConvictionOffenseAcdCode
                ? `${r.ConvictionOffenseAcdCode.Code ?? ''}${
                    r.ConvictionOffenseAcdCode.Description
                      ? ' - ' + r.ConvictionOffenseAcdCode.Description
                      : ''
                  }`
                : '',
            ]}
          />

          {/* Row 2 */}
          <GridTable
            title=""
            columns={[
              'ACD Detail',
              'CDL Holder',
              'CMV',
              'Hazmat',
              'Locator Reference',
              'State Code',
            ]}
            values={[
              r.ConvictionOffenseDetailAcd,
              splitOrSelf(r.CitationCdlHolderIndicator),
              splitOrSelf(r.ConvictionCommercialVehInd),
              splitOrSelf(r.ConvictionHazmatInd),
              r.ConvictionJurisCourtReportId,
              r.ConvictionJurisdictionOffenseCode,
            ]}
          />
        </View>
      ))}
    </View>
  );
}

function SectionWithdrawals({ list }) {
  const rows = Array.isArray(list) ? list : [];
  if (rows.length === 0) return null;

  return (
    <View>
      {rows.map((r, i) => (
        <View key={`wd-${i}`} >
          <Text style={S.sectionTitle}>Withdrawal {i + 1}</Text>

          {/* Row 1 */}
          <GridTable
            title=""
            columns={[
              'Effective Date',
              'Eligibility Date',
              'Rein. Date',
              'Withdrawal ID',
              'Linkage',
              'Action',
            ]}
            values={[
              r.DriverLicenseWithdrawalEffectiveDate,
              r.DRVLicWithdrawalEligibilityDate,
              r.DLWithdrawalReinstatementDate,
              r.DriverLicenseWithdrawalIdCode,
              r.DLWithdrawalIdConvictionLinksGroup,
              splitOrSelf(r.DriverLicenseWithdrawalType),
            ]}
          />

          {/* Row 2 */}
          <GridTable
            title=""
            columns={[
              'ACD Code',
              'State',
              'Basis',
              'Extent',
              'Locator Reference',
              'Reason Ref Code',
              'Due Process Status',
            ]}
            values={[
              r.DRVLicAcdWithdrawalReasonCode
                ? `${r.DRVLicAcdWithdrawalReasonCode.Code ?? ''}${
                    r.DRVLicAcdWithdrawalReasonCode.Description
                      ? ' - ' + r.DRVLicAcdWithdrawalReasonCode.Description
                      : ''
                  }`
                : '',
              r.JurisdictionCodeWithdrawing,
              splitOrSelf(r.DriverLicenseWithdrawalBasis),
              splitOrSelf(r.DRVLicWithdrawalExtentIdDlc),
              r.DRVLicWithdrawalReasonReference,
              r.DLWithdrawalJurisdictionReportId,
              splitOrSelf(r.DLWithdrawalDueProcessStatus),
            ]}
          />
        </View>
      ))}
    </View>
  );
}

function SectionAccidents({ list }) {
  const rows = Array.isArray(list) ? list : [];
  if (rows.length === 0) return null;

  return (
    <View>
      {rows.map((r, i) => (
        <View key={`acc-${i}`} >
          <Text style={S.sectionTitle}>Accident {i + 1}</Text>
          <GridTable
            title=""
            columns={['Accident Date', 'State', 'Severity', 'Driving CMV', 'Carrying Hazmat', 'Locator Reference']}
            values={[r.AccidentDate, r.AccidentJurisdictionCode, r.AccidentSeverityCode, r.DriverAccidentCommercialVehInd, r.DriverAccidentHazmatInd, r.AccidentLocatorReference]}
          />
        </View>
      ))}
    </View>
  );
}

function SectionAlias({ list }) {
  const rows = Array.isArray(list) ? list : [];
  if (rows.length === 0) return null;

  return (
    <View>
      {rows.map((r, i) => (
        <View key={`alias-${i}`} >
          <Text style={S.sectionTitle}>Alias {i + 1}</Text>
          <GridTable
            type="alias"
            title=""
            columns={['First Name','Middle Name','Last Name','Suffix','SSN','Birth Date', 'State', 'Driver License']}
            values={[r.PersonName.FirstName, r.PersonName.MiddleName, r.PersonName.SurName, r.PersonName.Suffix, 'XXXXXXXXX', r.DOB, r.License.JurisdictionOfIssue, r.License.LicenseNumber]}
          />
        </View>
      ))}
    </View>
  );
}

function CanadianSuspensions({ suspension }) {
  if (!suspension || !suspension.SuspensionTypes?.length) return null;

  const columns = [
    {
      header: 'Suspension Type',
      key: 'SuspensionType',
      render: r =>
        typeof r === 'string'
          ? r
          : (r.SuspensionType || r.Text || r.Value || r.Code || '')
    }
  ];

  return (
    <View>
      <GridTable
        title={`Canadian Suspension`}
        columns={['Suspension Start Date','Suspension End Date']}
        values={[suspension.StartDate, suspension.ExpiryDate]}
      />
      <ListTable
        title={``}
        columns={columns}
        rows={suspension.SuspensionTypes}
      />
    </View>
  );
}

// ---------- Main builder per type ----------
function buildDoc({ type, response, queryLine, messages, transactionID, driverPhotoBase64 }) {
  if (!response) return <Base title="Results" />;

  // Vehicle results (different shape)
  if (type === 'vehicle') {
    const txn = transactionID || response.TransactionId || 'N/A';
    const v = response.VehicleDetails || {};
    const plate = v.VehiclePlateDetails || {};
    const id = v.VehicleIdentificationDetails || {};
    const carrier = v.VehicleCarrierDetails || {};

    if(v.RFC) {
      return (
        <Base title="Vehicle Search Results" transactionId={txn} queryLine={queryLine}>
          <RowTable
            title="RFC"
            rows={[
              { label: 'RFC', value: v.RFC },
              { label: 'CURP', value: v.CURP },
              { label: 'US DOT #', value: v.USDOTNumber },
              { label: 'Name', value: v.Name },
              { label: 'Motor Carrier Status', value: v.MotorCarrierStatus },
              { label: 'Authorization Date', value: v.AuthorizationDate },
              { label: 'Street Address', value: v.StreetAddress },
              { label: 'Neighborhood(Colonia)', value: v.Neighborhood },
              { label: 'City/County', value: v.CityOrCounty },
              { label: 'State', value: v.State },
              { label: 'Postal Code', value: v.PostalCode },
              { label: 'Phone Number', value: v.PhoneNumber },
            ]}
          />
        </Base>
      );
    }
    return (
      <Base title="Vehicle Search Results" transactionId={txn} queryLine={queryLine}>
        <RowTable
          title="Plate"
          rows={[
            { label: 'Plate ID', value: plate.PlateId },
            { label: 'Status', value: plate.Status },
            { label: 'Type of Service', value: plate.TypeOfService },
          ]}
        />
        <RowTable
          title="Vehicle"
          rows={[
            { label: 'Vehicle ID', value: id.VehicleId },
            { label: 'Engine ID', value: id.EngineId },
            { label: 'Country Manufactured', value: id.CountryManufactured },
            { label: 'Type of Service', value: id.TypeOfService },
            { label: 'Class', value: id.Class },
            { label: 'Make', value: id.Make },
          ]}
        />
        <RowTable
          title=""
          rows={[
            { label: 'Year', value: id.Year },
            { label: 'Years Can Operate', value: id.YearsVehicleCanOperate },
            { label: 'Suspension', value: id.Suspension },
            { label: 'Steering', value: id.SteeringSuspension },
            { label: 'Trailing', value: id.TrailingSuspension },
            { label: 'Capacity', value: id.Capacity },
          ]}
        />
        <RowTable
          title=""
          rows={[
            { label: 'Capacity Units', value: id.CapacityInUnits },
            { label: 'Tires', value: id.NumberOfTires },
            { label: 'Axles', value: id.NumberOfAxles },
            { label: 'Height (m)', value: id.Height },
            { label: 'Length (m)', value: id.Length },
            { label: 'Weight (t)', value: id.Weight },
          ]}
        />
        <RowTable
          title=""
          rows={[
            { label: 'Gross Weight (t)', value: id.GrossWeight },
            { label: 'Width (m)', value: id.Width },
          ]}
        />
        <RowTable
          title="Carrier"
          rows={[
            { label: 'RFC', value: carrier.RFC },
            { label: 'CURP', value: carrier.CURP },
            { label: 'US DOT #', value: carrier.USDOTNumber },
            { label: 'Name', value: carrier.Name },
            { label: 'Type of Carrier', value: carrier.TypeOfCarrierService },
            { label: 'Federal Service', value: carrier.FederalService },
          ]}
        />
        <RowTable
          title=""
          rows={[
            { label: 'Private Service', value: carrier.PrivateService },
            { label: 'Street', value: carrier.StreetAddress },
            { label: 'Neighborhood', value: carrier.Neighborhood },
            { label: 'City/County', value: carrier.CityOrCounty },
            { label: 'State', value: carrier.State },
            { label: 'Postal Code', value: carrier.PostalCode },
          ]}
        />
        <RowTable
          title=""
          rows={[
            { label: 'Phone Number', value: carrier.PhoneNumber },
          ]}
        />
      </Base>
    );
  }

  // Person-based (aka, history, status, inquiry, minimal)
  const titles = {
    history: 'History Search Results',
    status:  'Status Search Results',
    inquiry: 'Inquiry Search Results',
    aka:     'AKA Lookup Search Results',
    minimal: 'Minimal Driver Search Results',
  };

  const txn = transactionID || response.TransactionId || 'N/A';
  const d = response.DriverDetails || {};
  const p = d.PersonDetails || {};
  const streetMailing =
    p.MailingAddress?.Street ||
    [p.MailingAddress?.Line1, p.MailingAddress?.Line2].filter(Boolean).join(' ').trim() ||
    '';
  const streetResidence =
    p.ResidenceAddress?.Street ||
    [p.ResidenceAddress?.Line1, p.ResidenceAddress?.Line2].filter(Boolean).join(' ').trim() ||
    '';
  const stateResident = countryOptions.US.find(id => id.code === p.ResidenceAddress?.State)?.label
    ?? countryOptions.CA.find(id => id.code === p.ResidenceAddress?.State)?.label
    ?? mxStates.find(id => id.code === p.ResidenceAddress?.State)?.label
    ?? p.ResidenceAddress?.State
  const stateMailing = countryOptions.US.find(id => id.code === p.MailingAddress?.State)?.label
    ?? countryOptions.CA.find(id => id.code === p.MailingAddress?.State)?.label
    ?? mxStates.find(id => id.code === p.MailingAddress?.State)?.label
    ?? p.MailingAddress?.State 
  const name = p.PersonName || {};
  const phys = p.PhysicalDetails || {};
  const lic = d.License || {};

  // robust accessors (handle typos/variants from upstream)
  const listConvictions = d.Convictions || lic.Convictions;
  const listAccidents   = d.Accidents   || lic.Accidents;
  const listWithdrawals = d.Withdrawals || d.Withdrawls || lic.Withdrawals || lic.Withdrawls;
  const listPermits     = d.Permits     || lic.Permits;
  const listRestrictions= d.Restrictions|| lic.Restrictions;
  const listAlias       = d.Alias       || lic.Alias;
  const listSuspensions = lic.Suspension;

  const countConv = lic.Total_ACD_ConvictionsOnRecord ?? listConvictions?.length ?? lic.Convictions?.length ?? '';
  const countAcc  = lic.TotalAccidentsOnRecord ?? listAccidents?.length ?? lic.Accidents?.length ?? '';
  const countWdl  = lic.TotalWithdrawalsOnRecord ?? listWithdrawals?.length ?? lic.Withdrawals?.length ?? '';
  const countPerm = lic.NumberOfPermits ?? listPermits?.length ?? lic.Permits?.length ?? '';
  const countRest = lic.NumberOfRestrictions ?? listRestrictions?.length ?? lic.Restrictions?.length ?? '';
  const country = queryLine.substring(1,3);
  return (
    <Base
      title={titles[type] || 'Results'}
      transactionId={txn}
      queryLine={queryLine}
      messages={messages}
      driverPhotoBase64={type === 'inquiry' ? driverPhotoBase64 : undefined}
    >
      {/* Driver General Information */}
      <GridTable
        title="Driver General Information"
        columns={['First Name','Middle Name','Last Name','Suffix']}
        values={[name.FirstName, name.MiddleName, name.SurName, name.Suffix]}
      />
      <GridTable
        type='DGI'
        title=""
        columns={[...(country === 'MX' ? ['CURP', 'RFC'] : ['SSN']),'Birth Date','Height','Weight','Eye Color','Sex']}
        values={[...(country === 'MX' ? [p.CURP, p.RFC] : ['XXXXXXXX']), p.DOB, phys.Height, phys.Weight, eyeFmt(phys.EyeColor), phys.Sex]}
      />

      {/* Residence */}
      {p.ResidenceAddress ? (
        <GridTable
          title="Residence"
          columns={['Street','City','County','State','Zip','Country']}
          values={[streetResidence, p.ResidenceAddress.City, p.ResidenceAddress.County, stateResident, p.ResidenceAddress.Zip, p.ResidenceAddress.Country]}
        />
      ) : null}

      {/* Mailing */}
      {p.MailingAddress ? (
        <GridTable
          title="Mailing Address"
          columns={['Street','City','County','State','Zip','Country']}
          values={[streetMailing, p.MailingAddress.City, p.MailingAddress.County, stateMailing, p.MailingAddress.Zip, p.MailingAddress.Country]}
        />
      ) : null}

      {/* Alias */}
      <SectionAlias list={listAlias} />
      {/* License */}
      {hasItems(lic) ? (
        <>
          <GridTable
            title="Driver License Details"
            columns={['Jurisdiction','Driver License','Issue Date','Exp. Date','Commercial Class']}
            values={[lic.JurisdictionOfIssue, lic.LicenseNumber, lic.IssueDate, lic.ExpirationDate ?? lic.ExpiryDate, lic.CommercialClassCode]}
          />
          <GridTable
            title=""
            columns={['Commercial Status','Non Commercial Class','Non Commercial Status','Withdrawal Action Pending']}
            values={[splitOrSelf(lic.CommercialStatus), lic.NonCommercialClassCode, splitOrSelf(lic.NonCommercialStatus), splitOrSelf(lic.WithdrawalActionPending)]}
          />
        </>
      ) : null}

      {/* Medical Certification */}
      <SectionMedicalCert med={d.MedicalCertificate} ex={d.MedicalExaminer} exName={d.MedicalExaminerName} medRest={d.MedicalCertificateRestrictionCode}/>

      {/* HazMat */}
      <SectionHazmat haz={d.HazMatEndorsement} />

      {/* Endorsements / Restrictions / Permits */}
      <SectionEndorsements items={d.Endorsements || lic.Endorsements} />
      <SectionRestrictions items={d.Restrictions || lic.Restrictions} />
      <SectionPermits items={d.Permits || lic.Permits} />

      {/* Counts row (if license present) */}
      {hasItems(lic) ? (
        <GridTable
          title=""
          columns={['Convictions','Accidents','Withdrawals','Permits','License Restrictions']}
          values={[countConv || '0', countAcc || '0', countWdl || '0', countPerm || '0', countRest || '0']}
        />
      ) : null}

      {/* AKA Record Creation Date */}
      {hasItems(p.RecordCreationDate) && (
        <GridTable
          title=""
          columns={['Central Site MPR Creation Date']}
          values={[p.RecordCreationDate]}
        />
      )}

      {/* Lists: Canadian Suspensions / Convictions / Accidents / Withdrawals */}
      <CanadianSuspensions suspension={listSuspensions} />
      <SectionConvictions list={listConvictions} />
      <SectionAccidents   list={listAccidents} />
      <SectionWithdrawals list={listWithdrawals} />
    </Base>
  );
}

// ---------- Public button ----------
export function PdfButton({ type, response, queryLine, messages, transactionID, driverPhotoBase64, fileName }) {
  const document = buildDoc({ type, response, queryLine, messages, transactionID, driverPhotoBase64 });
  const name = fileName || `${type || 'results'}_${Date.now()}.pdf`;
  return (
    <PDFDownloadLink document={document} fileName={name}>
      {({ loading }) => (
        <button type="button" className="btn btn-primary" id="button">
          {loading ? 'Preparing…' : 'Print'}
        </button>
      )}
    </PDFDownloadLink>
  );
}
